<script setup lang="ts">
   import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
   import { isBundledNativeOrigin, isNativeApp } from '~/functions/appOrigin';
   import { useAppRefresh } from '~/composables/useAppRefresh';

   /**
    * Drag down from the top of the screen to refresh — the app's missing F5 key.
    *
    * ## Why this is in JavaScript rather than a native control
    *
    * The genuine article is a `UIRefreshControl` on the WebView's scroll view, with the
    * Android equivalent beside it. That means Swift *and* Kotlin, or a third-party Capacitor
    * plugin, and none of it can be covered by the Vue test suite or iterated on without a
    * native rebuild. One implementation that behaves identically on both platforms was judged
    * the better trade while Android is not even signed yet.
    *
    * The cost is feel. WKWebView runs its own rubber-band overscroll at the top of the
    * document, so this gesture has to take the touch stream away from it — hence
    * `preventDefault` on a non-passive listener, and only while a pull is genuinely in
    * progress. Anything more aggressive would break ordinary scrolling.
    *
    * ## Why it renders nothing on the web
    *
    * Mobile Safari and Chrome already do this, and theirs triggers a real page load — which
    * is strictly better than anything we can do from inside the page. Two mechanisms on one
    * gesture would be worse than either alone. The gap is the packaged app, not mobile.
    */

   /** Pull past this to arm the refresh. Roughly the iOS feel. */
   const THRESHOLD_PX = 70;

   /** Past this the indicator stops following the finger, so a long drag cannot fling it. */
   const MAX_PULL_PX = 110;

   /** Finger travel is halved, which is what makes a pull feel weighted rather than loose. */
   const RESISTANCE = 0.5;

   const { isRefreshing, refresh } = useAppRefresh();

   const pull = ref(0);
   const isEnabled = ref(false);

   let startY = 0;
   let startX = 0;
   let tracking = false;

   /**
    * Visible once the pull is meaningful, fully opaque at the threshold — so the fade is the
    * signal that letting go will do something.
    */
   const opacity = computed(() => Math.min(pull.value / THRESHOLD_PX, 1));

   const isArmed = computed(() => pull.value >= THRESHOLD_PX);

   const indicatorStyle = computed(() => ({
      transform: `translateY(${pull.value}px)`,
      opacity: String(opacity.value),
      transition: tracking ? 'none' : 'transform 200ms ease-out, opacity 200ms ease-out',
   }));

   const onTouchStart = (event: TouchEvent) => {
      if (isRefreshing.value) return;

      // Only from a genuine resting position at the very top. Starting mid-scroll would
      // hijack an ordinary upward flick.
      if (window.scrollY > 0) return;

      if (event.touches.length !== 1) return;

      startY = event.touches[0].clientY;
      startX = event.touches[0].clientX;
      tracking = true;
   };

   const onTouchMove = (event: TouchEvent) => {
      if (!tracking) return;

      const deltaY = event.touches[0].clientY - startY;
      const deltaX = Math.abs(event.touches[0].clientX - startX);

      // A horizontal gesture belongs to whatever is underneath — carousels, the nav drawer.
      if (deltaX > Math.abs(deltaY)) return abandon();

      // Scrolling up, or the page moved under us. Either way this is not a pull.
      if (deltaY <= 0) return abandon();

      if (window.scrollY > 0) return abandon();

      // Takes the gesture away from WKWebView's rubber band. Requires a non-passive
      // listener, which is why they are registered explicitly below.
      event.preventDefault();

      pull.value = Math.min(deltaY * RESISTANCE, MAX_PULL_PX);
   };

   const onTouchEnd = async () => {
      if (!tracking) return;

      tracking = false;

      if (!isArmed.value) {
         pull.value = 0;
         return;
      }

      // Hold the indicator at the threshold while the work happens, rather than snapping
      // back and leaving the user unsure whether the pull registered.
      pull.value = THRESHOLD_PX;

      await refresh();

      pull.value = 0;
   };

   const abandon = () => {
      tracking = false;
      pull.value = 0;
   };

   onMounted(() => {
      isEnabled.value = isNativeApp() && isBundledNativeOrigin();

      if (!isEnabled.value) return;

      window.addEventListener('touchstart', onTouchStart, { passive: true });
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd, { passive: true });
      window.addEventListener('touchcancel', abandon, { passive: true });
   });

   onBeforeUnmount(() => {
      if (!isEnabled.value) return;

      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', abandon);
   });
</script>

<template>
   <!--
      Positioned against --nav-offset, the live nav height that useNavOffset publishes on the
      root element. A fixed value would sit under the nav on one breakpoint and float below it
      on another, and the nav also grows by the safe-area inset inside the app.
   -->
   <div
      v-if="isEnabled"
      data-testid="pull-to-refresh"
      class="pointer-events-none fixed inset-x-0 z-40 flex justify-center"
      :style="{ top: 'var(--nav-offset, 0px)' }">
      <div
         class="-mt-10 flex size-10 items-center justify-center rounded-full bg-white shadow-md"
         :style="indicatorStyle">
         <span
            class="pi pi-refresh text-brand-title"
            :class="{ 'animate-spin': isRefreshing }"
            data-testid="pull-to-refresh-icon"></span>
      </div>
   </div>
</template>
